/**
 * Subscription prices shown throughout the product.
 *
 * Keep these values in one place so trial prompts, the account page, and the
 * checkout selector cannot drift apart. The backend remains the authority for
 * entitlement; these values are presentation/plan-selection metadata.
 */
export const SUBSCRIPTION_PRICING = {
  monthly: {
    plan: 'monthly',
    label: 'Monthly',
    shortLabel: '1 month',
    termMonths: 1,
    amount: 999,
    monthlyEquivalent: 999,
    savings: 0,
    savingsPercent: 0,
    renewalLabel: 'Renews every month',
  },
  quarterly: {
    plan: 'quarterly',
    label: '3 months',
    shortLabel: '3 months',
    termMonths: 3,
    amount: 2799,
    monthlyEquivalent: 933,
    savings: 198,
    savingsPercent: 7,
    renewalLabel: 'Renews every 3 months',
  },
  semiannual: {
    plan: 'semiannual',
    label: '6 months',
    shortLabel: '6 months',
    termMonths: 6,
    amount: 5199,
    monthlyEquivalent: 867,
    savings: 795,
    savingsPercent: 13,
    renewalLabel: 'Renews every 6 months',
  },
  annual: {
    plan: 'annual',
    label: '12 months',
    shortLabel: 'Annual',
    termMonths: 12,
    amount: 9990,
    monthlyEquivalent: 833,
    savings: 1998,
    savingsPercent: 17,
    renewalLabel: 'Renews every year',
  },
} as const;

export type SubscriptionPlan = keyof typeof SUBSCRIPTION_PRICING;

const INR_FORMATTER = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

export function formatInr(amount: number): string {
  return INR_FORMATTER.format(amount);
}

export function formatMinorCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 0,
  }).format(amount / 100);
}

export function planLabel(plan: SubscriptionPlan | null | undefined): string {
  return plan ? SUBSCRIPTION_PRICING[plan]?.label ?? 'Subscription' : 'Subscription';
}

export const PRICING_SUMMARY =
  `Plans start at ${formatInr(SUBSCRIPTION_PRICING.monthly.amount)}/month. Save up to ${SUBSCRIPTION_PRICING.annual.savingsPercent}% with annual billing.`;

// Compatibility exports for existing conversion prompts. These names predate
// the finalized four-term catalog; the copy now describes the actual prices
// without presenting them as a temporary discount or crossed-out anchor.
export const INTRODUCTORY_PRICING_LINE = PRICING_SUMMARY;
export const REGULAR_PRICING_LINE =
  `${formatInr(SUBSCRIPTION_PRICING.quarterly.amount)} for 3 months · ${formatInr(SUBSCRIPTION_PRICING.semiannual.amount)} for 6 months · ${formatInr(SUBSCRIPTION_PRICING.annual.amount)} for 12 months.`;
